import React from 'react';
import { FaAward, FaUsers, FaCheckCircle, FaClock } from 'react-icons/fa';
import { useInView } from '../hooks/useInView';

const About = () => {
  const sectionRef = useInView();
  const stats = [
    { icon: <FaClock />, number: '10+', label: 'Anos de Experiência' },
    { icon: <FaUsers />, number: '500+', label: 'Clientes Atendidos' },
    { icon: <FaCheckCircle />, number: '98%', label: 'Satisfação' },
    { icon: <FaAward />, number: '100%', label: 'Garantia' }
  ];

  const differentials = [
    {
      title: 'Profissionais Qualificados',
      description: 'Nossa equipe é formada por mecânicos certificados e com vasta experiência no mercado automotivo.'
    },
    {
      title: 'Equipamentos Modernos',
      description: 'Investimos constantemente em tecnologia de ponta para oferecer diagnósticos precisos e serviços de qualidade.'
    },
    {
      title: 'Peças Originais',
      description: 'Trabalhamos apenas com peças originais e de fornecedores confiáveis, garantindo durabilidade e segurança.'
    },
    {
      title: 'Preço Justo',
      description: 'Oferecemos orçamentos transparentes e competitivos, sem surpresas ou custos ocultos.'
    },
    {
      title: 'Atendimento Personalizado',
      description: 'Cada cliente é único. Oferecemos soluções personalizadas para as necessidades do seu veículo.'
    },
    {
      title: 'Garantia Total',
      description: 'Todos os nossos serviços possuem garantia, demonstrando nossa confiança no trabalho realizado.'
    }
  ];

  return (
    <section id="sobre" className="py-20 bg-white" ref={sectionRef}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="animate-on-scroll text-center max-w-3xl mx-auto mb-16">
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">Sobre Nós</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-dark mt-3 mb-4">
            Tradição e Qualidade em Mecânica Automotiva
          </h2>
          <p className="text-gray-600 text-lg">
            Há mais de 10 anos cuidando do seu veículo com excelência, 
            dedicação e compromisso com a sua segurança.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`animate-on-scroll zoom delay-${(index + 1) * 100} bg-gradient-to-br from-secondary to-secondary-dark rounded-xl p-6 text-center text-white transform hover:scale-105 transition-transform duration-300`}
            >
              <div className="text-3xl mb-2 flex justify-center text-white">
                {stat.icon}
              </div>
              <div className="text-3xl md:text-4xl font-bold mb-1">{stat.number}</div>
              <div className="text-sm text-gray-200">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Story Section */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div className="animate-on-scroll from-left">
            <h3 className="text-2xl md:text-3xl font-bold text-secondary-dark mb-4">
              Nossa História
            </h3>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Nossa oficina nasceu do sonho de oferecer serviços automotivos 
                de qualidade com atendimento humanizado e preços justos. O que começou como uma 
                pequena oficina de bairro, hoje é referência na região.
              </p>
              <p>
                Ao longo dos anos, investimos continuamente em capacitação profissional, 
                equipamentos modernos e na satisfação dos nossos clientes. Cada veículo que 
                passa por nossas mãos recebe o mesmo cuidado e atenção, independente do modelo 
                ou marca.
              </p>
              <p>
                Nossa missão é simples: cuidar do seu carro como se fosse nosso, garantindo 
                sua segurança e tranquilidade nas estradas. Acreditamos que a confiança se 
                constrói com trabalho sério, transparência e resultados consistentes.
              </p>
            </div>
          </div>

          <div className="animate-on-scroll from-right relative">
            <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="/assets/about-mechanic.png" 
                alt="Mecânico da Box 50 trabalhando"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Differentials */}
        <div>
          <h3 className="text-2xl md:text-3xl font-bold text-secondary-dark mb-8 text-center">
            Nossos Diferenciais
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentials.map((item, index) => (
              <div
                key={index}
                className={`animate-on-scroll delay-${Math.min((index % 3 + 1) * 100, 300)} bg-box-50 rounded-xl p-6 border border-gray-100 hover:border-accent/30 hover:shadow-lg transition-all duration-300`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-secondary-dark mb-2">{item.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Badge */}
        <div className="mt-16 bg-gradient-to-r from-secondary-dark to-black rounded-2xl p-8 border border-primary/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                <FaAward className="text-3xl text-secondary-dark" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-white mb-1">
                  Certificados e Qualificados
                </h4>
                <p className="text-gray-300">
                  Nossa equipe possui certificações técnicas reconhecidas no mercado
                </p>
              </div>
            </div>
            <a
              href="#servicos"
              className="bg-accent hover:bg-accent-dark text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 whitespace-nowrap"
            >
              Conheça Nossos Serviços
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
